import {Nullable, Optional} from '../../../../Common/TypeHelper'
import {MBAdminError} from '../../../Utils/MBAdminError'
import {Contract} from '../../../Utils/Resources'

export type CreditCardInfoJSON = {
  cardId: string,
  cardNumber: string,
  expireDate: string
}

export type BucketMemberInfoJSON = {
  contract: {
    now: Nullable<ContractInfoJson>
    next: Nullable<ContractInfoJson>
  }
} & Pick<BucketMemberInfo,
  'leaveInfo' |
  'mainContract' |
  'stage' |
  'subscribeMailMagazineIdList'>

export type LeaveInfo = {
  leaveOrderDate: Nullable<string>,
  leaveDate: Nullable<string>,
  bucketLeaveReason: Nullable<string>,
}

export type MainContract = {
  bucketMemberId: Nullable<string>,
  startDate: string,
  executionDateTime: string,
  enrollmentReason: Nullable<string>,
}

export type ContractInfoJson = Omit<ContractInfo, 'contractMethod' | 'contractType'> &
  {
    paymentMethodDetail: PaymentMethodDetailJson,
    method: ContractMethod,
    type: Contract.ContractTypeType
  }

type PaymentMethodDetailJson = Partial<CreditCardInfo & MobileCarrierInfo & ConvenienceStoreInfo>

export type ContractInfo = {
  executionDate: string,
  sourceType: Contract.SourceTypeType,
  status: Contract.StatusType,
  bucketId: number,
  bucketKey: string,
  bucketName: string,
  bucketMemberId: Nullable<string>,
  amount: number,
  startDate: string,
  endDate: string,
  term: string,
  contractMethod: CreditCardMethod | MobileCarrierMethod | ConvenienceStoreMethod | FreeBucketMethod
  contractType: Contract.ContractTypeType
  mainContractId: string
  contractId: string
  gmoOrderId: Nullable<string>
}

export type ContractMethod = 'credit_card' | 'mobile_carrier' | 'convenience_store' | 'none'

export type CreditCardMethod = {
  method: 'credit_card'
  paymentMethodDetail: CreditCardInfo
}

export type MobileCarrierMethod = {
  method: 'mobile_carrier'
  paymentMethodDetail: MobileCarrierInfo
}

export type ConvenienceStoreMethod = {
  method: 'convenience_store'
  paymentMethodDetail: ConvenienceStoreInfo
}

export type FreeBucketMethod = {
  method: 'none',
  paymentMethodDetail: null
}

export type CreditCardInfo = CreditCardInfoJSON

export type MobileCarrierKey = 'docomo' | 'softbank' | 'au'

export type MobileCarrierInfo = {
  carrierKey: Optional<MobileCarrierKey>
}

export type ConvenienceStoreKey =
  'lawson' |
  'familymart' |
  'ministop' |
  'seicomart' |
  ''

export type ConvenienceStoreInfo = {
  convenienceStoreKey: ConvenienceStoreKey
  paymentTerm: string
}

export interface BucketMemberInfo {
  leaveInfo: LeaveInfo
  mainContract: MainContract
  contract: {
    now: Nullable<ContractInfo>
    next: Nullable<ContractInfo>
  }
  stage: Nullable<Stage>
  subscribeMailMagazineIdList: number[]
}

export type Stage = {
  rank: number
  name: string
}

export class BucketMemberInfoDTO implements BucketMemberInfo {
  leaveInfo: LeaveInfo
  mainContract: MainContract
  contract: {
    now: Nullable<ContractInfo>
    next: Nullable<ContractInfo>
  }
  stage: Nullable<Stage>
  subscribeMailMagazineIdList: number[]

  constructor(
    leaveInfo: LeaveInfo,
    mainContract: MainContract,
    contract: { now: Nullable<ContractInfo>; next: Nullable<ContractInfo> },
    stage: Nullable<Stage>,
    subscribeMailMagazineIdList: number[],
  ) {
    this.leaveInfo = leaveInfo
    this.mainContract = mainContract
    this.contract = contract
    this.stage = stage
    this.subscribeMailMagazineIdList = subscribeMailMagazineIdList
  }

  static fromJSON(jsonObject: BucketMemberInfoJSON): BucketMemberInfoDTO {
    return new BucketMemberInfoDTO(
      jsonObject.leaveInfo,
      jsonObject.mainContract,
      {
        now: getContract(jsonObject.contract.now),
        next: getContract(jsonObject.contract.next),
      },
      jsonObject.stage,
      jsonObject.subscribeMailMagazineIdList,
    )
  }
}

export const getContract = (
  contractInfoJson: Nullable<ContractInfoJson>,
): Nullable<ContractInfo> => {
  if (!contractInfoJson) {
    return null
  }

  const {method, paymentMethodDetail, type, ...rest} = contractInfoJson
  return {
    ...rest,
    contractMethod: getMethod(method, paymentMethodDetail),
    contractType: type,
  }
}

export const getMethod = (method: ContractMethod, paymentMethodDetail: PaymentMethodDetailJson):
  CreditCardMethod | MobileCarrierMethod | ConvenienceStoreMethod | FreeBucketMethod => {
  if (method === 'credit_card') {
    return {
      method: 'credit_card',
      paymentMethodDetail: {
        cardId: paymentMethodDetail.cardId ?? '',
        cardNumber: paymentMethodDetail.cardNumber ?? '',
        expireDate: paymentMethodDetail.expireDate ?? '',
      },
    }
  }

  if (method === 'mobile_carrier') {
    return {
      method: 'mobile_carrier',
      paymentMethodDetail: {
        carrierKey: paymentMethodDetail.carrierKey,
      },
    }
  }

  if (method === 'convenience_store') {
    return {
      method: 'convenience_store',
      paymentMethodDetail: {
        convenienceStoreKey: paymentMethodDetail.convenienceStoreKey ?? '',
        paymentTerm: paymentMethodDetail.paymentTerm ?? '',
      },
    }
  }

  if (method === 'none') {
    return {
      method: method,
      paymentMethodDetail: null,
    }
  }

  throw new MBAdminError('SystemError')
}
